/**
 * Check-in API client. Thin wrappers over the shared `apiFetch` helper (see
 * ./api) that map to the Elysia /checkins module.
 */
import type {
  CheckInDto,
  CheckInResult,
  CheckoutInput,
  GuardianDto,
  LabelPayload,
  LabelTemplateDto,
  NewCheckInInput,
  NewLabelTemplateInput,
  NewStationInput,
  RosterEntryDto,
  StationDto,
  UpdateLabelTemplateInput,
  UpdateStationInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  CheckInDto,
  CheckInResult,
  CheckoutInput,
  GuardianDto,
  LabelPayload,
  LabelTemplateDto,
  NewCheckInInput,
  NewStationInput,
  RosterEntryDto,
  StationDto,
} from '@fc/core'

// ---- Stations ----
export const listStations = () => apiFetch<StationDto[]>('/checkins/stations')

export const getStation = (id: string) => apiFetch<StationDto>(`/checkins/stations/${id}`)

export const createStation = (input: NewStationInput) =>
  apiFetch<StationDto>('/checkins/stations', { method: 'POST', body: JSON.stringify(input) })

export const updateStation = (id: string, input: UpdateStationInput) =>
  apiFetch<StationDto>(`/checkins/stations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteStation = (id: string) =>
  apiFetch<void>(`/checkins/stations/${id}`, { method: 'DELETE' })

export const pairStation = (id: string) =>
  apiFetch<{ deviceToken: string }>(`/checkins/stations/${id}/pair`, { method: 'POST' })

// ---- Check-in / Checkout ----
export const checkIn = (input: NewCheckInInput) =>
  apiFetch<CheckInResult>('/checkins/check-in', { method: 'POST', body: JSON.stringify(input) })

export const checkOut = (input: CheckoutInput) =>
  apiFetch<CheckInDto>('/checkins/check-out', { method: 'POST', body: JSON.stringify(input) })

export const voidCheckIn = (id: string) =>
  apiFetch<CheckInDto>(`/checkins/check-ins/${id}/void`, { method: 'POST' })

export const listGuardians = (personId: string) =>
  apiFetch<GuardianDto[]>(`/checkins/people/${personId}/guardians`)

// ---- Roster ----
export const getRoster = (occurrenceId: string, activeOnly = false) =>
  apiFetch<RosterEntryDto[]>(`/checkins/roster/${occurrenceId}${activeOnly ? '?active=true' : ''}`)

export const getCheckInLabels = (id: string) =>
  apiFetch<LabelPayload[]>(`/checkins/check-ins/${id}/labels`)

// ---- Label templates ----
export const listLabelTemplates = () => apiFetch<LabelTemplateDto[]>('/checkins/label-templates')

export const createLabelTemplate = (input: NewLabelTemplateInput) =>
  apiFetch<LabelTemplateDto>('/checkins/label-templates', {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateLabelTemplate = (id: string, input: UpdateLabelTemplateInput) =>
  apiFetch<LabelTemplateDto>(`/checkins/label-templates/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteLabelTemplate = (id: string) =>
  apiFetch<void>(`/checkins/label-templates/${id}`, { method: 'DELETE' })
